import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, BookOpen, Bell, Palette, Type, Save, RotateCcw, Camera, AlertCircle, CreditCard, ArrowUpCircle, HelpCircle, RefreshCw, Cloud, CloudOff } from 'lucide-react';
import { useUserProfile, ReadingTheme } from '../hooks/useUserProfile';
import { useFeatureGating } from '../hooks/useFeatureGating';
import { createPortalSession } from '../services/stripe';
import { getSyncStatus, manualSync, SyncStatus } from '../services/syncService';

interface UserSettingsProps {
  onClose?: () => void;
}

export const UserSettings: React.FC<UserSettingsProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const {
    settings,
    updateSettings,
    updatePreferences,
    updateNotifications,
    setDisplayName,
    setBio,
    setReadingGoal,
    resetToDefaults,
    getAvatar,
    getMembershipTier,
    membershipProfile
  } = useUserProfile();
  const { hasCustomThemes } = useFeatureGating();
  const dbTier = getMembershipTier();
 
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'notifications' | 'sync'>('profile');
  const [localName, setLocalName] = useState(settings.displayName);
  const [localBio, setLocalBio] = useState(settings.bio);
  const [localGoal, setLocalGoal] = useState(settings.readingGoal);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [isManaging, setIsManaging] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);
  
  // Sync state (Ultra only)
  const [syncStatus, setSyncStatus] = useState<SyncStatus>(getSyncStatus(dbTier));

  const handleManageSubscription = async () => {
    if (!membershipProfile?.id) {
      setManageError('Unable to find your account. Please try again.');
      return;
    }
    
    setIsManaging(true);
    setManageError(null);
    
    try {
      const url = await createPortalSession(membershipProfile.id);
      if (url) {
        window.location.href = url;
      } else {
        setManageError('Unable to open subscription settings. Please try again.');
      }
    } catch (error) {
      setManageError('Failed to open subscription settings. Please try again.');
    } finally {
      setIsManaging(false);
    }
  };

  const isPaidMember = dbTier && ['starter', 'reader', 'ultra'].includes(dbTier);

  const handleSaveProfile = () => {
    setDisplayName(localName);
    setBio(localBio);
    setReadingGoal(localGoal);
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 2000);
    }, 500);
  };

  const avatar = getAvatar();

  const tabs = [
    { id: 'profile' as const, label: 'Profile', icon: User },
    { id: 'preferences' as const, label: 'Preferences', icon: Palette },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell },
    ...(dbTier === 'ultra' ? [{ id: 'sync' as const, label: 'Sync', icon: Cloud }] : [])
  ];

  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header with Avatar */}
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center text-2xl font-bold text-blue-600">
              {avatar}
            </div>
            <button className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-gray-600 hover:text-blue-600 transition-colors">
              <Camera className="h-4 w-4" />
            </button>
          </div>
          <div className="text-white">
            <h2 className="text-xl font-bold">{settings.displayName}</h2>
            <p className="text-blue-100 text-sm">{settings.email || 'No email set'}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <div className="flex">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Display Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={localName}
                  onChange={(e) => setLocalName(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your name"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => updateSettings({ email: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Bio
              </label>
              <textarea
                value={localBio}
                onChange={(e) => setLocalBio(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Tell us about yourself..."
              />
            </div>

            <div className="flex gap-3 pt-4">
              <button
                onClick={handleSaveProfile}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={resetToDefaults}
                className="flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Reset
              </button>
              {isPaidMember && (
                <button
                  onClick={handleManageSubscription}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ml-auto"
                >
                  <CreditCard className="h-4 w-4" />
                  {isManaging ? 'Opening...' : 'Manage Subscription'}
                </button>
              )}
              {!isPaidMember && (
                <button
                  onClick={() => navigate('/upsell')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors ml-auto"
                >
                  <ArrowUpCircle className="h-4 w-4" />
                  Upgrade Plan
                </button>
              )}
            </div>

            {manageError && (
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm mt-2">
                <AlertCircle className="h-4 w-4" />
                {manageError}
              </div>
            )}

            {showSaved && (
              <p className="text-green-600 dark:text-green-400 text-sm mt-2">Settings saved successfully!</p>
            )}

            {/* Support Link */}
            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Need help?{' '}
                <a 
                  href="mailto:support@flashread.com" 
                  className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                >
                  Contact Support
                </a>
              </p>
            </div>
          </div>
        )}

        {activeTab === 'preferences' && (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Default Reading Speed (WPM)
              </label>
              <input
                type="range"
                min={100}
                max={2000}
                step={25}
                value={settings.preferences.defaultWpm}
                onChange={(e) => updatePreferences({ defaultWpm: parseInt(e.target.value) })}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>100</span>
                <span className="font-medium text-blue-600">{settings.preferences.defaultWpm} WPM</span>
                <span>2000</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Daily Reading Goal
              </label>
              <div className="relative">
                <BookOpen className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="number"
                  value={localGoal}
                  onChange={(e) => setLocalGoal(parseInt(e.target.value) || 0)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min={0}
                  step={500}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Words per day</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Font Size
              </label>
              <div className="flex gap-2">
                {(['small', 'medium', 'large'] as const).map(size => (
                  <button
                    key={size}
                    onClick={() => updatePreferences({ fontSize: size })}
                    className={`px-4 py-2 rounded-lg capitalize transition-colors ${
                      settings.preferences.fontSize === size
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Font Family
              </label>
              <select
                value={settings.preferences.fontFamily}
                onChange={(e) => updatePreferences({ fontFamily: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="system-ui">System Default</option>
                <option value="serif">Serif</option>
                <option value="monospace">Monospace</option>
                <option value="sans-serif">Sans Serif</option>
                {hasCustomThemes() && (
                  <>
                    <option value="Merriweather, serif">Merriweather</option>
                    <option value="Lora, serif">Lora</option>
                    <option value="Source Serif 4, serif">Source Serif</option>
                    <option value="PT Serif, serif">PT Serif</option>
                    <option value="Crimson Text, serif">Crimson Text</option>
                    <option value="Libre Baskerville, serif">Libre Baskerville</option>
                    <option value="IBM Plex Serif, serif">IBM Plex Serif</option>
                    <option value="Bitter, serif">Bitter</option>
                    <option value="Nunito, sans-serif">Nunito</option>
                    <option value="Atkinson Hyperlegible, sans-serif">Atkinson Hyperlegible</option>
                  </>
                )}
              </select>
            </div>

            {/* Reading Theme - Reader/Ultra only */}
            {hasCustomThemes() && (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Reading Theme
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {([
                    { value: 'light' as ReadingTheme, name: 'Light', bg: 'bg-white', text: 'text-slate-900' },
                    { value: 'dark' as ReadingTheme, name: 'Dark', bg: 'bg-slate-900', text: 'text-white' },
                    { value: 'sepia' as ReadingTheme, name: 'Sepia', bg: 'bg-amber-50', text: 'text-amber-900' },
                    { value: 'midnight' as ReadingTheme, name: 'Midnight', bg: 'bg-indigo-950', text: 'text-indigo-200' },
                    { value: 'forest' as ReadingTheme, name: 'Forest', bg: 'bg-green-50', text: 'text-green-900' },
                    { value: 'ocean' as ReadingTheme, name: 'Ocean', bg: 'bg-cyan-50', text: 'text-cyan-900' },
                  ] as const).map((themeOption) => (
                    <button
                      key={themeOption.value}
                      onClick={() => updatePreferences({ theme: themeOption.value })}
                      className={`p-3 ${themeOption.bg} ${themeOption.text} rounded-lg border-2 transition-colors text-center ${
                        settings.preferences.theme === themeOption.value
                          ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-50'
                          : 'border-transparent hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                    >
                      <span className="text-xs font-medium">{themeOption.name}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-2">Choose your preferred reading theme (Reader tier benefit)</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'notifications' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Weekly Progress</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Receive weekly summaries of your reading progress</p>
              </div>
              <button
                onClick={() => updateNotifications({ weeklyProgress: !settings.notifications.weeklyProgress })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.notifications.weeklyProgress ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                  settings.notifications.weeklyProgress ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Achievements</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Get notified when you unlock new achievements</p>
              </div>
              <button
                onClick={() => updateNotifications({ achievements: !settings.notifications.achievements })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.notifications.achievements ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                  settings.notifications.achievements ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Achievement Modals</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Show full-screen celebration popups for achievements</p>
              </div>
              <button
                onClick={() => {
                  const current = localStorage.getItem('flashread_achievement_modals_disabled') === 'true';
                  localStorage.setItem('flashread_achievement_modals_disabled', String(!current));
                }}
                className={`w-12 h-6 rounded-full transition-colors ${
                  localStorage.getItem('flashread_achievement_modals_disabled') !== 'true' ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                  localStorage.getItem('flashread_achievement_modals_disabled') !== 'true' ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Reminders</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Daily reminders to practice your reading</p>
              </div>
              <button
                onClick={() => updateNotifications({ reminders: !settings.notifications.reminders })}
                className={`w-12 h-6 rounded-full transition-colors ${
                  settings.notifications.reminders ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                  settings.notifications.reminders ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
          </div>
        )}

        {activeTab === 'sync' && dbTier === 'ultra' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-700">
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Sync Status</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {syncStatus === 'synced' && 'All data synced with cloud'}
                  {syncStatus === 'pending' && 'Changes pending sync'}
                  {syncStatus === 'offline' && 'No internet connection'}
                  {syncStatus === 'syncing' && 'Syncing data...'}
                  {syncStatus === 'error' && 'Sync failed, try again'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {syncStatus === 'synced' && <Cloud className="h-5 w-5 text-green-500" />}
                {syncStatus === 'pending' && <Cloud className="h-5 w-5 text-yellow-500" />}
                {syncStatus === 'offline' && <CloudOff className="h-5 w-5 text-gray-500" />}
                {syncStatus === 'syncing' && <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />}
                {syncStatus === 'error' && <AlertCircle className="h-5 w-5 text-red-500" />}
              </div>
            </div>


            <button
              onClick={async () => {
                setSyncStatus('syncing');
                const status = await manualSync(membershipProfile?.id || '', dbTier);
                setSyncStatus(status);
              }}
              disabled={syncStatus === 'syncing'}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
              {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
